import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { Quote, ClientAccumulator } from '@/lib/database.types';
import { QuoteStatusBadge } from '@/components/quotes/QuoteStatusBadge';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ROLE_LABELS: Record<string, { text: string; cls: string }> = {
  admin:        { text: 'ADMIN',        cls: 'bg-red-500/15 text-red-300 border border-red-500/30' },
  distribuidor: { text: 'DISTRIBUIDOR', cls: 'bg-orange/15 text-orange border border-orange/30' },
  cliente:      { text: 'CLIENTE',      cls: 'bg-blue-500/15 text-blue-300 border border-blue-500/30' },
};

export function AccountDrawer({ open, onClose }: Props) {
  const { user, profile, isAdmin, signOut } = useAuth();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [acc, setAcc] = useState<ClientAccumulator | null>(null);

  useEffect(() => {
    if (!open || !user) return;
    void (async () => {
      const [q, a] = await Promise.all([
        supabase.from('quotes').select('*').eq('client_id', user.id)
          .order('created_at', { ascending: false }).limit(6),
        supabase.from('client_accumulators').select('*').eq('client_id', user.id).maybeSingle(),
      ]);
      setQuotes(q.data ?? []);
      setAcc(a.data ?? null);
    })();
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!profile) return null;
  const role = ROLE_LABELS[profile.role] ?? ROLE_LABELS.cliente;
  const initial = profile.name.charAt(0).toUpperCase();

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[940] bg-ink/70 backdrop-blur-sm transition-opacity duration-300
          ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />
      <aside
        className={`fixed inset-y-0 right-0 z-[950] w-full max-w-md bg-ink-100 border-l border-white/10
          flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="p-6 border-b border-white/5 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-12 w-12 rounded-full bg-orange grid place-items-center font-display text-2xl text-white shrink-0">
              {initial}
            </div>
            <div className="min-w-0">
              <div className="font-display tracking-wider2 truncate">{profile.name}</div>
              <div className="text-xs text-white/50 truncate">{profile.email}</div>
              <span className={`badge-pill mt-1.5 ${role.cls}`}>{role.text}</span>
            </div>
          </div>
          <button onClick={onClose} aria-label="Cerrar"
            className="text-white/60 hover:text-white text-xl shrink-0">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar p-6 space-y-6">
          {/* Acumulador kg (resumen) */}
          {acc && (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-ink border border-white/5 p-4">
                <div className="text-[10px] text-white/40 uppercase tracking-wider2 mb-1">Total</div>
                <div className="font-display text-2xl text-orange">{acc.kg_total} <span className="text-sm text-white/40">kg</span></div>
              </div>
              <div className="bg-ink border border-white/5 p-4">
                <div className="text-[10px] text-white/40 uppercase tracking-wider2 mb-1">Libre</div>
                <div className="font-display text-2xl text-green-400">{acc.kg_libre} <span className="text-sm text-white/40">kg</span></div>
              </div>
            </div>
          )}

          {/* Últimas cotizaciones */}
          <div>
            <div className="text-xs font-display tracking-wider3 text-white/50 mb-3">ÚLTIMAS COTIZACIONES</div>
            {quotes.length === 0 ? (
              <p className="text-sm text-white/40">
                Aún no tienes cotizaciones. Ve al{' '}
                <Link to="/catalogo" onClick={onClose} className="text-orange hover:underline">catálogo</Link>.
              </p>
            ) : (
              <div className="space-y-2">
                {quotes.map((q) => (
                  <div key={q.id} className="bg-ink border border-white/5 p-3 flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-display tracking-wider2 truncate">
                        #{q.id.slice(0, 8).toUpperCase()} · {q.total_kg} kg
                      </div>
                      <div className="text-[10px] text-white/40">
                        {new Date(q.created_at).toLocaleDateString('es-MX')}
                      </div>
                    </div>
                    <QuoteStatusBadge status={q.status} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link to="/dashboard" onClick={onClose} className="btn btn-fill !text-sm !py-2 w-full text-center block">
            VER HISTORIAL COMPLETO →
          </Link>

          {isAdmin && (
            <Link to="/admin" onClick={onClose} className="btn !text-sm !py-2 w-full text-center block">
              <i className="fas fa-shield-halved mr-2" /> PANEL ADMIN
            </Link>
          )}
        </div>

        <div className="p-6 border-t border-white/5">
          <button
            onClick={async () => { await signOut(); onClose(); }}
            className="w-full py-2 border border-white/15 hover:border-red-400 hover:text-red-300 font-display tracking-wider2 text-sm transition-colors"
          >
            <i className="fas fa-arrow-right-from-bracket mr-2" /> CERRAR SESIÓN
          </button>
        </div>
      </aside>
    </>
  );
}
